import {
  Currency,
  deserialize,
  epochToView,
  epochToViewUTC,
  Exclude,
  Expose,
  plainToClass,
  Rental,
  Serializable,
  SerializableData,
  TaxV2,
  TaxV3,
  Type,
} from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { upgradeInvoiceTax } from '../../utils/tv2-invoice-tax'
import * as uuid from 'uuid'
import * as costResolver from '@tokeet/cost-resolver'
import * as lodash from 'lodash'

export enum InvoiceTypes {
  BookingInvoice = 0,
  GeneralInvoice = 1,
}

export enum InvoiceStatus {
  Canceled = -1,
  Unsent = 0,
  Unpaid = 1,
  Paid = 2,
  Refunded = 3,
}

export interface InvoiceIntegrationsAttributes {
  holded_exported_at?: number
  holded_invoice_id?: string
  [key: string]: unknown
}

export interface InvoiceAttributes {
  integrations?: InvoiceIntegrationsAttributes
  [key: string]: unknown
}

export class PaymentDetails extends Serializable<PaymentDetails> {
  date: number
  id: string
  description: string
  service: string
  total: number

  @Expose({ name: 'card_type' })
  cardType: string

  @Expose({ name: 'card_num' })
  cardNum: string

  static deserialize(data: object): PaymentDetails {
    return deserialize(PaymentDetails, data)
  }
}

export class Invoice extends Serializable<Invoice> {
  @Expose({ name: 'paid_date' })
  paidDate: number

  @Expose({ name: 'invoice_items' })
  @Type(() => InvoiceItem)
  invoiceItems: InvoiceItem[]

  @Expose({ name: 'guest_name' })
  guestName: string

  @Expose({ name: 'guest_id' })
  guestId: string

  @Expose({ name: 'pkey' })
  id: string

  @Expose({ name: 'rental_name' })
  rentalName: string

  @Expose({ name: 'public_key' })
  publicKey: string

  @Expose({ name: 'invoice_date' })
  invoiceDate: number

  @Expose({ name: 'expires_at' })
  expiresAt: number

  @Expose({ name: 'invoice_rule' })
  invoiceRule: any

  @Expose({ name: 'payment_terms' })
  paymentTerms: string

  @Expose({ name: 'payment_instructions' })
  paymentInstructions: string

  @Expose({ name: 'payment_details' })
  @Type(() => PaymentDetails)
  paymentDetails: PaymentDetails

  @Expose({ name: 'inquiry_id' })
  inquiryId: string

  @Expose({ name: 'due_date' })
  dueDate: number

  @Expose({ name: 'rental_id' })
  rentalId: string

  @Expose({ name: 'invoice_num' })
  invoiceNum: string

  @Expose({ name: 'online_payment' })
  onlinePayment: number

  @Expose({ name: 'gateway_id' })
  gatewayId: string

  @Expose({ name: 'address_key' })
  addressKey?: string

  currency: Currency

  @Type(() => Rental)
  rental: Rental

  archived: number
  type: InvoiceTypes
  @Type(() => Number)
  status: InvoiceStatus
  attributes?: InvoiceAttributes
  account: number
  created: number
  sent: number
  scheduled: number
  notes: string
  invoice_header: string

  createdView: string
  sentView: string
  invoiceDateView: string
  invoiceDueDateView: string
  invoiceTotal: number
  invoiceStatus: string
  currencySymbol: string

  static deserialize(data: any): Invoice {
    let invoice: Invoice = plainToClass<Invoice, Invoice>(Invoice, data)

    invoice = upgradeInvoiceTax(invoice)

    invoice.createdView = epochToView(invoice.created)
    invoice.invoiceDateView = epochToViewUTC(invoice.invoiceDate)
    invoice.invoiceDueDateView = epochToViewUTC(invoice.dueDate)
    invoice.sentView = epochToView(invoice.sent)
    invoice.invoiceStatus = getInvoiceStatus(invoice.status)
    invoice.currencySymbol = R.pathOr('$', ['symbol'], invoice.currency)

    return invoice
  }
}

export class InvoiceItem extends Serializable<InvoiceItem> {
  @Exclude({ toPlainOnly: true })
  id: string // frontend fix

  item: string
  qty: number
  description: string
  unitCost: number
  discount: number
  percent: number = 1 // payment percent

  tax: number // v1 percent
  @Type(() => TaxV2)
  taxEx: TaxV2 // v2 percent and flat
  @Type(() => TaxV3)
  taxes: TaxV3[] // v3 name, percent and flat

  constructor(data?: SerializableData<InvoiceItem>) {
    super(data)
    this.id = uuid.v4()
    this.taxes = []
  }

  static deserialize(data: object): InvoiceItem {
    const item = deserialize<InvoiceItem>(InvoiceItem, data)
    if (!item.id) {
      item.id = uuid.v4()
    }

    return item
  }
}

export interface InvoiceTaxWithTotal {
  name: string
  modality: costResolver.TaxModalities
  type: costResolver.TaxTypes
  amount: number //  "10"
  calculated_amount: number //  "26.60",
  display_text: string //  "10.00% per person per night",
}

export interface InvoiceLineItemWithTotal {
  line_number: number // 1,
  description: string // "3 Bed Oslo Palace - updated_18",
  nights: number // 1,
  quantity: number // 1,
  nightly_rate: number // "133.00"
  percent: number // 1, // partial payment

  fees: []
  discount: number // 0,
  is_discount: 0 | 1 // 0, if this item is a discount
  taxes: InvoiceTaxWithTotal[]

  subtotal: number // "133.00",
  line_tax_total: number // "29.26",
  line_total: number // "162.26",
}

export interface InvoiceTotalResponse {
  inquiry_id?: string
  pkey?: string
  line_items: InvoiceLineItemWithTotal[]
  subtotal: number
  total: number
  total_tax: number
}

export function isInvoiceTotalChanged(lineItems1: InvoiceItem[], lineItems2: InvoiceItem[]) {
  if (lineItems1?.length !== lineItems2?.length) return true
  const numberKeys = ['qty', 'unitCost', 'discount', 'percent', 'taxes']
  return !!lodash.find(lineItems1, (line, index) => {
    const item1 = {
      taxes: [],
      ...lodash.pickBy(line, (value, key) => !lodash.isNil(value) && numberKeys.includes(key)),
    }
    const item2 = {
      taxes: [],
      ...lodash.pickBy(lineItems2[index], (value, key) => !lodash.isNil(value) && numberKeys.includes(key)),
    }
    return !lodash.isEqual(item1, item2)
  })
}

export function getInvoiceStatus(status: InvoiceStatus) {
  switch (status) {
    case InvoiceStatus.Canceled:
      return $localize`Canceled`
    case InvoiceStatus.Unsent:
      return $localize`Unsent`
    case InvoiceStatus.Unpaid:
      return $localize`Unpaid`
    case InvoiceStatus.Paid:
      return $localize`Paid`
    case InvoiceStatus.Refunded:
      return $localize`Refunded`
    default:
      return $localize`Unsaved`
  }
}

export interface CheckoutSession {
  display_items: CheckoutSessionDisplayItem[]
  object: 'checkout.session'
  subscription: any
  client_reference_id: string // this is set in payments, it should be invoice_num
  success_url: string
  billing_address_collection: any
  locale: any
  livemode: boolean
  mode: string
  setup_intent: any
  customer: any
  payment_intent: string
  id: string
  payment_method_types: string[]
  cancel_url: string
  customer_email: any
  submit_type: any
}

export interface CheckoutSessionDisplayItem {
  type: string
  currency: string
  custom: any
  amount: number
  quantity: number
}

export interface InitiatePeachCheckoutResponse {
  redirectUrl: string
  checkoutId?: string
  nonce: string
  merchantTransactionId: string
}

export interface InvoiceLineItemTitles {
  item: string
  unit: string
  qty: string
  discountFees: string
  tax: string
  description: string
}

export const invoiceLineItemTitlesByType: { [type: string]: InvoiceLineItemTitles } = {
  [InvoiceTypes.BookingInvoice]: {
    item: $localize`Rental`,
    unit: $localize`Nightly Rate`,
    qty: $localize`Nights`,
    discountFees: $localize`Discount/Fees`,
    tax: $localize`Taxes`,
    description: $localize`Description`,
  },
  [InvoiceTypes.GeneralInvoice]: {
    item: $localize`Item`,
    unit: $localize`Cost`,
    qty: $localize`Quantity`,
    discountFees: $localize`Discount/Fees`,
    tax: $localize`Taxes`,
    description: $localize`Description`,
  },
}
